import { observer } from 'mobx-react-lite'
import { useCallback } from 'react'
import { StyleSheet, TouchableOpacity } from 'react-native'
import { useThrottledCallback } from 'use-debounce'

import { useFocusEffect, useRouter } from 'expo-router'

import { DS } from '../../../design-system'
import { useCreateGame } from '../../../hooks/use-create-game'
import { useCountdown } from '../../../hooks/useCountdown'
import { translate } from '../../../i18n'
import { useStore } from '../../../models'
import { GameModeTag } from '../../../models/api-store'
import { getLocalTimeForTomorrowAtSix, monthAndDayFormatter } from '../../../utils'
import { events } from '../../../models/analytics/analytics-events'

enum GameState {
  NotStarted = 'GAME_NOT_STARTED',
  NotFinished = 'GAME_NOT_FINISHED',
  Finished = 'GAME_FINISHED'
}

function getGameState(canPlay: boolean, canShare: boolean): GameState {
  if (canPlay && !canShare) {
    return GameState.NotStarted
  }
  if (!(canPlay || canShare)) {
    return GameState.NotFinished
  }
  if (!canPlay && canShare) {
    return GameState.Finished
  }
  throw new Error('Invalid game state')
}

export const DailyPokerish = observer(() => {
  const router = useRouter()
  const {
    analytics: { setEvent },
    game: { canPlayThreeCard, getCanPlayThreeCard, setGameKind, getThreeCardResult }
  } = useStore()
  const { loading, createGame } = useCreateGame()
  const date = new Date()
  const formattedDate = monthAndDayFormatter(date)
  const tomorrowAtSix = getLocalTimeForTomorrowAtSix()
  const [, hours, minutes] = useCountdown(tomorrowAtSix)

  const handlePress = useThrottledCallback(() => {
    if (canPlayThreeCard?.canPlay) {
      setEvent(events.threeCard.viewed)
      setGameKind(GameModeTag.threeCard)
      createGame({ gameKind: GameModeTag.threeCard })
    } else if (canPlayThreeCard?.canShare) {
      router.navigate('/game/three-card/game-over')
    } else {
      return
    }
  }, 1000)

  const fetchResults = useCallback(async () => {
    await getThreeCardResult()
  }, [getThreeCardResult])

  const verifyIfCanPlay = useCallback(async () => {
    await getCanPlayThreeCard()
  }, [getCanPlayThreeCard])

  const gameState = getGameState(!!canPlayThreeCard?.canPlay, !!canPlayThreeCard?.canShare)

  useFocusEffect(
    useCallback(() => {
      verifyIfCanPlay()
      fetchResults()
    }, [fetchResults, verifyIfCanPlay])
  )

  return (
    <TouchableOpacity disabled={loading || gameState === GameState.NotFinished} onPress={handlePress}>
      <DS.Frame
        gap="8"
        style={[
          styles.container,
          gameState === GameState.NotFinished ? styles.containerNotFinished : styles.containerWithShadow
        ]}
      >
        <DS.Frame gap="8" style={styles.rightContent}>
          {gameState === GameState.NotStarted || gameState === GameState.NotFinished ? (
            <DS.Frame style={styles.chips}>
              <DS.Icon name="Chips-blue-green" size={24} style={styles.firstChip} />
              <DS.Icon name="Chips-orange-purple" size={24} style={styles.secondChip} />
            </DS.Frame>
          ) : (
            <DS.Icon name={'Check--enclosed'} size={24} color={'Brand/Purple'} />
          )}
          {gameState === GameState.NotFinished ? (
            <DS.Text variant="Body Medium/Medium">{translate('homeScreen.notCompletedPokerish')}</DS.Text>
          ) : (
            <DS.Text variant="Body Medium/Medium">{translate('homeScreen.dailyPokerish')}</DS.Text>
          )}
        </DS.Frame>
        <DS.Frame gap="8" style={styles.leftContent}>
          {gameState === GameState.NotStarted && <DS.Text variant="Caption/Regular">{formattedDate}</DS.Text>}
          {gameState === GameState.NotFinished && (
            <DS.Text variant="Caption/Regular">{`${hours}h ${minutes}m`}</DS.Text>
          )}
          {gameState === GameState.Finished && (
            <DS.Text variant="Caption/Regular">{translate('homeScreen.seeResultsPokerish')}</DS.Text>
          )}
          {gameState !== GameState.NotFinished && <DS.Icon name="Arrow---right" size={16} color="Global/Black" />}
        </DS.Frame>
      </DS.Frame>
    </TouchableOpacity>
  )
})

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: DS.palette['Global/White'],
    borderRadius: 24,
    padding: DS.spacing[12]
  },
  containerWithShadow: {
    shadowColor: DS.palette['Global/Black'],
    shadowOffset: {
      width: 0,
      height: 4
    },
    shadowOpacity: 0.05
  },
  containerNotFinished: {
    backgroundColor: DS.palette['Brand/Light Grey']
  },
  rightContent: {
    flexDirection: 'row',
    alignItems: 'center'
  },
  leftContent: {
    flexDirection: 'row',
    alignItems: 'center'
  },
  chips: {
    flexDirection: 'row'
  },
  firstChip: {
    zIndex: 1
  },
  secondChip: {
    marginLeft: -DS.spacing[12]
  }
})
